import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import SubTitleBar from "../../../components/SubTitleBar/SubTitleBar";
import TableTitleBar from "../../../components/TableTitleBar/TableTitleBar";

import organizationViewStyles from "./OrganizationView.module.scss";
import OrganizationViewSurveyTab from "./OrganizationViewSurveyTab/OrganizationViewSurveyTab";
import OrganizationProfileTab from "./OrganizationProfileTab/OrganizationProfileTab";
import {
  clearOrganizationSurveyTabState,
  organizationSurveyTabSelector,
  organizationChartThunk,
  surveysDropdownThunk,
  yearsDropdownThunk,
} from "../../../slices/SuperAdmin/OrganizationSurveyTab.slice";
import {
  clearOrganizationProfileTabState,
  getOrganizationProfileDetailsThunk,
  organizationProfileTabSelector,
} from "../../../slices/SuperAdmin/OrganizationProfileTab.slice";
import ControlLibrary from "../../Control_library/LinkList/ControlLibrary";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { checkIsStateAdmin } from "../../../helpers/utils";

const TABS = [
  { key: "assessments", label: "Assessments" },
  { key: "profile", label: "Organization Profile" },
  { key: "controls", label: "Control Library" },
];

export default function OrganizationView() {
  const { id, email } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const organizationSurveyTabState = useSelector(organizationSurveyTabSelector);
  const organizationProfileTabState = useSelector(
    organizationProfileTabSelector
  );
  const isStateAdminLoggedIn = checkIsStateAdmin();

  const [defaultYear, setDefaultYear] = useState(null);
  const [defaultSurvey, setDefaultSurvey] = useState(null);
  const [activeTab, setActiveTab] = useState("assessments");
  const fromStateAdminManagement =
    location?.state?.fromStateAdminManagement || false;

  useEffect(() => {
    handleSurveyTabInit();
    handleProfileTabInit();

    return handleCleanUp;
  }, []);

  useEffect(() => {
    if (!organizationSurveyTabState.years) return;

    setDefaultYear(organizationSurveyTabState.years[0]?.year ?? "- -");
  }, [organizationSurveyTabState.years]);

  useEffect(() => {
    if (!organizationSurveyTabState.surveys) return;

    setDefaultSurvey(organizationSurveyTabState.surveys[0]?.id ?? "- -");
  }, [organizationSurveyTabState.surveys]);

  const handleCleanUp = () => {
    dispatch(clearOrganizationSurveyTabState());
    dispatch(clearOrganizationProfileTabState());
  };

  const handleSurveyTabInit = () => {
    dispatch(yearsDropdownThunk({ organizationId: id }))
      .then((data) => {
        const years = (data.payload.data || []).map((d) => d.year);
        return dispatch(
          surveysDropdownThunk({
            year: years[0] ?? 0,
            organizationId: id,
          })
        );
      })
      .then((data) => {
        dispatch(
          organizationChartThunk({
            surveyId: data.payload.data[0]?.id ?? 0,
            orgId: id,
          })
        );
      });
  };

  const handleProfileTabInit = () => {
    dispatch(
      getOrganizationProfileDetailsThunk({ page: 1, pageSize: 10, email })
    );
  };

  const handleBackBtnClick = () => {
    if (isStateAdminLoggedIn) {
      history.push({
        pathname: `/state-admin-organizations`,
      });
    } else if (fromStateAdminManagement) {
      history.push({
        pathname: `/super-admin-member-organizations-list/${fromStateAdminManagement?.id}`,
        state: { stateAdmin: fromStateAdminManagement },
      });
    } else {
      history.push({
        pathname: `/super-admin`,
      });
    }
  };

  const handleSurveyOnChange = (value) => {
    setDefaultSurvey(value);
    dispatch(organizationChartThunk({ surveyId: value, orgId: id }));
  };

  const handleYearOnChange = (value) => {
    setDefaultYear(value);
    dispatch(
      surveysDropdownThunk({
        year: value,
        organizationId: id,
      })
    ).then((data) => {
      dispatch(
        organizationChartThunk({
          surveyId: data.payload.data[0]?.id ?? 0,
          orgId: id,
        })
      );
    });
  };

  return (
    <main className="OrganizationView ops-page">
      <SubTitleBar showBackBtn onBackBtnClick={handleBackBtnClick}>
        Organization Management
      </SubTitleBar>
      <div className={`${organizationViewStyles.container} container`}>
        <TableTitleBar
          title={
            organizationProfileTabState.organizationProfile
              ?.organization_name ?? "--"
          }
        />
        <div className={organizationViewStyles.tab}>
          <nav className="ops-tab-strip" aria-label="Organization sections">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`ops-tab ${activeTab === tab.key ? "active" : ""}`}
                aria-current={activeTab === tab.key ? "page" : undefined}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {activeTab === "assessments" ? (
            <OrganizationViewSurveyTab
              isSurveysLoading={organizationSurveyTabState.isSurveysLoading}
              isYearsLoading={organizationSurveyTabState.isYearsLoading}
              years={organizationSurveyTabState.years?.map((y) => y.year)}
              surveys={organizationSurveyTabState.surveys}
              chartData={organizationSurveyTabState.chartData}
              defaultSurvey={defaultSurvey}
              defaultYear={defaultYear}
              onSurveyChange={handleSurveyOnChange}
              onYearsChange={handleYearOnChange}
              isChartLoading={organizationSurveyTabState.isChatLoading}
            />
          ) : null}

          {activeTab === "profile" ? (
            <OrganizationProfileTab
              organisationProfile={organizationProfileTabState}
            />
          ) : null}

          {activeTab === "controls" ? (
            <span className="platform-admin-library">
              <ControlLibrary isPlatformAdminView orgId={id} />
            </span>
          ) : null}
        </div>
      </div>
    </main>
  );
}
